import React, { useEffect, useState, useRef } from "react";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { ProductBatchSummaryItem } from "../../models/productbatch-model";
import productBatchService from "../../services/productbatch/productBatchService";
import { downloadQr } from "../../utils/qr";
import ProductBatchVisibilityDialog from "./ProductBatchVisibilityDialog";



interface FilterParams {
  sourceTypeId?: number | null;
  vendorId?: number | null;
  subCategoryId?: number | null;
  productId?: number | null;
  manufacturingUnitId?: number | null;
  manufactureDateFrom?: string | null;
  manufactureDateTo?: string | null;
  expiryDateFrom?: string | null;
  expiryDateTo?: string | null;
  posId?: number | null;
  onlyAvailableStockLessThanOne?: boolean | null;
  onlyHidden?: boolean | null;
}

interface SortParams {
  sortBy?: string | null;
  sortOrder?: "asc" | "desc" | null;
}

interface Props {
  data: ProductBatchSummaryItem[];
  totalRecords: number;
  pageNumber: number;
  pageSize: number;
  filters: FilterParams;
  sortParams: SortParams;
  setLoading: (loading: boolean) => void;
  onPageChange: (pageNumber: number, pageSize: number) => void;
  onSortChange: (sortBy: string | null, sortOrder: "asc" | "desc" | null) => void;
  onEdit: (batch: ProductBatchSummaryItem) => void;
  onDataRefresh: () => void;
}

const ProductBatchDataTable: React.FC<Props> = ({
  data,
  totalRecords,
  pageNumber,
  pageSize,
  filters,
  sortParams,
  setLoading,
  onPageChange,
  onSortChange,
  onEdit,
  onDataRefresh,
}) => {
  const [records, setRecords] = useState<ProductBatchSummaryItem[]>([]);
  const toast = useRef<Toast>(null);
  const [visibilityDialogVisible, setVisibilityDialogVisible] = useState(false);
  const [selectedBatchForVisibility, setSelectedBatchForVisibility] = useState<ProductBatchSummaryItem | null>(null);


  const startIndex = totalRecords > 0 ? (pageNumber - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(pageNumber * pageSize, totalRecords);

  const paginatorTemplate = {
    layout: "RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport",
    CurrentPageReport: () => (
      <span className="mx-3 text-sm text-gray-600">
        Showing {startIndex} to {endIndex} of {totalRecords} entries
      </span>
    ),
  };

  useEffect(() => {
    if (Array.isArray(data)) {
      setRecords([...data]);
    }
  }, [data]);

  const handlePageChange = (e: DataTablePageEvent) => {
    const newPageNumber = e.page! + 1;
    const newPageSize = e.rows;
    onPageChange(newPageNumber, newPageSize);
  };

  const handleSort = (e: DataTableSortEvent) => {
    if (e.sortField) {
      const sortOrder = e.sortOrder === 1 ? "asc" : "desc";
      onSortChange(e.sortField, sortOrder);
    } else {
      onSortChange(null, null);
    }
  };

  const handleDeleteClick = (batch: ProductBatchSummaryItem) => {
    confirmDialog({
      message: `Are you sure you want to delete batch ${batch.batchCode}?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          setLoading(true);
          await productBatchService.delete(batch.batchId);
          toast.current?.show({
            severity: "success",
            summary: "Success",
            detail: "Batch deleted successfully",
            life: 3000,
          });
          onDataRefresh();
        } catch (error: any) {
        console.error("Error deleting batch:", error);

        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Failed to delete batch";

        toast.current?.show({
          severity: "error",
          summary: "Delete Failed",
          detail: errorMessage,
          life: 3000,
        });
      } finally {
        setLoading(false);
      }
      },
    });
  };


  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatNumber = (value?: number | null, digits = 2) => {
    if (value === null || value === undefined || Number.isNaN(value)) return 0;
    return Number(Number(value).toFixed(digits));
  };

  const btnBorder = "border border-gray-300 rounded-md";
  const actionBodyTemplate = (rowData: ProductBatchSummaryItem) => {
    const hasReport = rowData.hasReport === true;

    return (
      <div className="flex gap-2 justify-center">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-warning"
          aria-label="Edit"
          onClick={() => onEdit(rowData)}
          style={{ width: "2rem", height: "2rem" }}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger"
          aria-label="Delete"
          onClick={() => handleDeleteClick(rowData)}
          style={{ width: "2rem", height: "2rem" }}
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-qrcode"
          className={`p-button-text ${btnBorder}`}
          tooltip={
            hasReport
              ? "Download QR Code"
              : "Download QR Code (Test report not available)"
          }
          tooltipOptions={{ position: "left" }}
          onClick={() => {
            let qrPayload: string;

            if (hasReport) {

              // ✅ REAL URL when report exists
              const baseUrl = (process.env.REACT_APP_URL || "").replace(/\/+$/, "");
              qrPayload = `${baseUrl}/public/report/${rowData.batchId}`;
            } else {

              // 🔒 SAFE TEXT — no URL
              qrPayload = `${rowData.batchId}`; qrPayload = `B${String(rowData.batchId).padStart(8, "0")}`;
            }

            downloadQr(
              qrPayload,
              `QR_Batch_${rowData.batchCode}.png`,
              rowData.batchCode
            );

            toast.current?.show({
              severity: "success",
              summary: "QR Downloaded",
              detail: hasReport
                ? "QR downloaded"
                : "QR downloaded (no test report available)",
              life: 2000,
            });
          }}
        />
        {(filters.onlyAvailableStockLessThanOne === true || filters.onlyHidden === true) && (
          <Button
            icon={rowData.isHidden ? "pi pi-eye-slash" : "pi pi-eye"}
            className={`p-button-text ${rowData.isHidden ? 'p-button-danger' : 'p-button-success'}`}
            aria-label={rowData.isHidden ? "Unhide" : "Hide"}
            onClick={() => {
              setSelectedBatchForVisibility(rowData);
              setVisibilityDialogVisible(true);
            }}
            style={{ width: "2rem", height: "2rem", border: "none" }}
            tooltip={rowData.isHidden ? "Unhide" : "Hide"}
            tooltipOptions={{ position: "top" }}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <div className="col-12">
        <div className="card border-noround p-2">
          <DataTable
            value={records}
            paginator
            lazy
            rows={pageSize}
            first={(pageNumber - 1) * pageSize}
            totalRecords={totalRecords}
            paginatorTemplate={paginatorTemplate}
            rowsPerPageOptions={[5, 10, 20, 50]}
            onPage={handlePageChange}
            onSort={handleSort}
            sortField={sortParams.sortBy || ""}
            sortOrder={
              sortParams.sortOrder === "asc" ? 1 : sortParams.sortOrder === "desc" ? -1 : 0
            }
            removableSort
            responsiveLayout="scroll"
            scrollable
          >
            <Column
              field="batchCode"
              header="Batch Code"
              sortable
              frozen
              style={{ minWidth: "200px" }}
            />
             <Column
              field="productName"
              header="Product"
              sortable
              frozen
              style={{ minWidth: "200px" }}
            />
            <Column
              field="sourceTypeName"
              header="Source Type"
              sortable
              style={{ minWidth: "120px" }}
            />
            <Column
              field="vendorName"
              header="Vendor"
              sortable
              style={{ minWidth: "150px" }}
            />
            <Column
              field="subCategoryName"
              header="Sub Category"
              sortable
              style={{ minWidth: "150px" }}
            />
            <Column
              field="byProductName"
              header="Byproduct"
              sortable
              body={(rowData) =>
                rowData.sourceTypeName?.toLowerCase() === "trading" ? "" : rowData.byProductName || ""
              }
              style={{ minWidth: "150px" }}
            />
           
            <Column
              field="manufactureDate"
              header="Manufacture Date"
              sortable
              body={(rowData) => formatDate(rowData.manufactureDate)}
              style={{ minWidth: "130px" }}
            />
            <Column
              field="manufacturingUnitAddress"
              header="Manufacturing Unit"
              sortable
              body={(rowData) =>
                rowData.manufacturingUnitAddress || "-"
              }
              style={{ minWidth: "200px" }}
            />
            <Column
              field="expiryDate"
              header="Expiry Date"
              sortable
              body={(rowData) => formatDate(rowData.expiryDate)}
              style={{ minWidth: "130px" }}
            />
            <Column
              field="stock"
              header="Stock"
              sortable
              body={(rowData) => formatNumber(rowData.stock)}
              style={{ minWidth: "80px", textAlign: "right" }}
            />
            <Column
              header="Allocated Stock"
              body={(rowData) => {
                const stock = rowData.stock ?? 0;
                const available = rowData.availableStock ?? 0;
                const diff = stock - available;
                return formatNumber(diff);
              }}
              sortable
              style={{ minWidth: "120px", textAlign: "right" }}
            />
            <Column
              field="availableStock"
              header="Available Stock"
              sortable
              body={(rowData) => formatNumber(rowData.availableStock)}
              style={{ minWidth: "120px", textAlign: "right" }}
            />
            <Column
              field="lastModifiedBy"
              header="Last Modified By"
              sortable
              style={{ minWidth: "150px" }}
            />
            <Column
              field="lastModifiedOn"
              header="Last Modified On"
              sortable
              body={(rowData) => {
                if (!rowData.lastModifiedOn) return "-";
                const date = new Date(rowData.lastModifiedOn);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              }}
              style={{ minWidth: "160px" }}
            />
            <Column
              header="Actions"
              body={actionBodyTemplate}
              frozen
              alignFrozen="right"
              headerStyle={{ textAlign: "center" }}
              style={{ minWidth: "130px" }} // Increased minWidth slightly to fit 4 buttons nicely
            />
          </DataTable>
        </div>
      </div>
      {selectedBatchForVisibility && (
        <ProductBatchVisibilityDialog
          visible={visibilityDialogVisible}
          batchId={selectedBatchForVisibility.batchId}
          batchCode={selectedBatchForVisibility.batchCode}
          targetHidden={!selectedBatchForVisibility.isHidden}
          onClose={() => {
            setVisibilityDialogVisible(false);
            setSelectedBatchForVisibility(null);
          }}
          onSuccess={() => {
            setVisibilityDialogVisible(false);
            setSelectedBatchForVisibility(null);
            onDataRefresh();
          }}
          setLoading={setLoading}
        />
      )}
    </>
  );
};

export default ProductBatchDataTable;
